import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  surface?: '1' | '2' | '3'
  hoverable?: boolean
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, surface = '1', hoverable = false, children, ...props }, ref) => {
    const surfaceClasses = {
      '1': 'bg-surface-1 border-border-subtle',
      '2': 'bg-surface-2 border-border',
      '3': 'bg-surface-3 border-border',
    }

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-sm border p-5 sm:p-6 md:p-8 transition-all duration-200',
          surfaceClasses[surface],
          hoverable &&
            'hover:border-border-strong hover:bg-surface-2 hover:-translate-y-0.5',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Card.displayName = 'Card'

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('mb-3 sm:mb-4 flex flex-col space-y-1.5', className)} {...props}>
      {children}
    </div>
  )
)
CardHeader.displayName = 'CardHeader'

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-lg md:text-xl font-semibold tracking-tight text-fg', className)}
      {...props}
    >
      {children}
    </h3>
  )
)
CardTitle.displayName = 'CardTitle'

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm text-fg-subtle leading-relaxed', className)}
      {...props}
    >
      {children}
    </p>
  )
)
CardDescription.displayName = 'CardDescription'

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('text-sm text-fg-subtle', className)} {...props}>
      {children}
    </div>
  )
)
CardContent.displayName = 'CardContent'
